import { useTranslation } from 'next-i18next';
import styled from 'styled-components';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { getFilterOptions, getFooterLinks } from '../../new-lib/apis';
import ReachUs from '../../features/contact/components/organic-form/ReachUs';
import { GetServerSidePropsContext } from 'next';
import { useRouter } from 'next/router';
import FormHeader from '../../features/contact/components/organic-form/FormHeader';
import FormFooter from '../../features/contact/components/organic-form/FormFooter';
import { getSelectorsByUserAgent } from 'react-device-detect';
import dynamic from 'next/dynamic';
import { ConfigsApis } from '../../new-lib/apis/configs.apis';

const ContactFormContainer = dynamic(
  () =>
    import(
      '../../features/contact/components/organic-form/ContactFormContainer'
    ),
  { ssr: false }
);

interface ContactProps {
  isMobile: boolean;
}

export default function Contact({ isMobile }: ContactProps) {
  const { t } = useTranslation('contact');

  const { query } = useRouter();
  return (
    <>
      {!isMobile && (
        <Container>
          <div className="container-fluid">
            <h1 className="contact-headline">{query.contact_slug}</h1>
            <Background>
              <div className="headline">
                <h2 className="text-3">{t('contact_us')}</h2>
              </div>
            </Background>

            <div className="contact-section">
              <div className="contact-details">
                <ReachUs />
              </div>
              <div className="form-container">
                <ContactFormContainer
                  renderedInputs={{
                    name: true,
                    location: true,
                    message: true,
                    mobileNumber: true
                  }}
                  isContactPage={true}
                  layout={{
                    header: <FormHeader hasIcon={true} />,
                    footer: <FormFooter />
                  }}
                />
              </div>
            </div>
          </div>
        </Container>
      )}

      {isMobile && (
        <>
          <ContactMobileTitle>{query.contact_slug}</ContactMobileTitle>
          <Background></Background>
          <Container>
            <div className="container-fluid">
              <div className="form-container">
                <ContactFormContainer
                  renderedInputs={{
                    name: true,
                    location: true,
                    message: true,
                    mobileNumber: true
                  }}
                  isContactPage={true}
                  layout={{
                    header: <FormHeader hasIcon={true} />,
                    footer: <FormFooter />
                  }}
                />
              </div>
              <div className="contact-details">
                <ReachUs />
              </div>
            </div>
          </Container>
        </>
      )}
    </>
  );
}

const Container = styled.div`
  width: 100%;
  min-height: 70vh;
  .container-fluid {
    .contact-section {
      width: 90%;
      margin: 0 auto;
      display: flex;
      flex-direction: row;
      justify-content: space-between;
      position: relative;
      min-height: 400px;
      .form-container {
        width: 540px;
        position: absolute;
        top: -60%;
        inset-inline-end: 0;
      }
    }
    .contact-headline {
      font-size: ${props => props.theme.font.sizes.xxxl};
      color: ${props => props.theme.light.colors.primaryBlue};
      font-family: materialBold;
      margin-top: 50px;
    }
  }

  @media screen and (max-width: ${props =>
      props.theme.breakPoints.tablet.min}) {
    min-height: unset;
    transform: translateY(100px);
    .container-fluid {
    }
  }
`;

const Background = styled.div`
  position: relative;
  width: 100%;
  height: 367px;
  background-image: url('/assets/images/common/contact-background.webp');
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
  display: flex;
  align-items: center;
  justify-content: start;
  border-radius: ${props => props.theme.borderRadius};
  margin-top: 38px;

  .headline {
    position: absolute;
    bottom: 30px;
    inset-inline-start: 60px;
    h1,
    h2 {
      color: white;
      font-size: ${props => props.theme.font.sizes.xxxl};
    }

    h2 {
      margin-top: 5px;
    }
  }

  @media screen and (max-width: ${props =>
      props.theme.breakPoints.tablet.min}) {
    height: 203px;
    border-radius: 0;
    margin-top: 0;
    position: absolute;
    z-index: -1;
  }
`;

const ContactMobileTitle = styled.h1`
  font-size: ${props => props.theme.font.sizes.xl};
  color: ${props => props.theme.light.colors.primaryBlue};
  font-family: materialBold;
  margin: 25px 0px 25px 0px;
  margin-inline-start: 16px;
`;

export async function getServerSideProps({
  req,
  locale
}: GetServerSidePropsContext) {
  try {
    const isMobile = getSelectorsByUserAgent(
      req.headers['user-agent']!
    ).isMobile;

    const [footerLinks, configVariables, filterOptions] = await Promise.all([
      getFooterLinks(locale, 'contact-slug'),
      ConfigsApis.getConfigs(locale),
      getFilterOptions(locale)
    ]);

    return {
      props: {
        ...(await serverSideTranslations(locale as string, [
          'contact',
          'common'
        ])),
        filterOptions: JSON.stringify(filterOptions) || '',
        configVariables,
        isMobile,
        experimentVariant: req.cookies['_ab'],
        layout: {
          footerLinks,
          url: `${process.env.NEXT_PUBLIC_HOST}/contact`,
          isMobile
        }
      }
    };
  } catch (err) {
    return {
      notFound: true
    };
  }
}
